import { Box, Button, Container, Grid, LinearProgress, Typography, useTheme } from "@mui/material"
import React, { useState } from "react"
import { useEncodeProgress } from "../services/api/hooks/useEncodeProgress"
import { useVideoAPI } from "../services/api/hooks/useVideoAPI"
import { VideoQueryType, VideoType } from "../services/api/video"
import { VideoPlayer } from "../components/VideoPlayer"
import AspectRatioBox from "../components/AspectRatioBox"
import { httpClient } from "../services/http"
import { useSnackbarContext } from "../services/snackbar"
import { VideoThumbnailImage } from "../components/VideoThumbnail"
import { useNavigate } from "react-router-dom"
import { PageTitle, TagChip } from "../components/StyledElements"
import { ClickAwayListener, Unstable_Popup as Popup } from "@mui/base"
import { FormatDate, FormatParagraphs } from "../components/Util"
import { useDirectAPI } from "../services/api/hooks/useDirectAPI"
import { DirectQueryType } from "../services/api/direct"
import { useTagAPI } from "../services/api/hooks/useTagAPI"

const RES_PROGRESS_THRESHOLDS: Record<string, number> = { "240p": 3, "480p": 15, "720p": 41, "1080p": 100 }

export const PendingVideos = (): React.ReactElement => {
  const { videosData, refresh } = useVideoAPI({
    constraint: VideoQueryType.PENDING,
  })
  const { open: sbOpen } = useSnackbarContext()
  const navigate = useNavigate()

  const encodeAllVideos = async () => {
    try {
      await Promise.all(
        videosData
          .filter((video) => video.processing === 1)
          .map(async (video) => {
            try {
              await httpClient.instance.post(`encode/${video.id}`)
            } catch {
              sbOpen("Error pendant l'encodage", "error")
            }
          }),
      )
      refresh()
    } catch (error) {
      sbOpen("Une erreur s'est produite", "error")
    }
  }

  return (
    <>
      <PageTitle>{"Vidéos en attente"}</PageTitle>
      {videosData && (
        <Container maxWidth="xl">
          <Box m={4} gap={1}>
            <Button sx={{ margin: 1 }} variant="contained" color="primary" onClick={() => navigate("/addVideo")}>
              {"Ajouter une vidéo"}
            </Button>
            {videosData.filter((video) => video.processing == 1).length > 0 && (
              <Button sx={{ margin: 1 }} variant="contained" color="secondary" onClick={() => encodeAllVideos()}>
                {"Tout encoder et publier"}
              </Button>
            )}
          </Box>
          {videosData.length > 0 ? (
            videosData.map((video) => <PendingVideoElement video={video} refresh={refresh} key={video.id} />)
          ) : (
            <Typography variant="h5" color="textSecondary" m={4} mb={0} pb={4}>
              {"Aucune vidéo en attente"}
            </Typography>
          )}
        </Container>
      )}
    </>
  )
}

interface PendingVideoElementProps {
  video: VideoType
  refresh: () => void
}

const PendingVideoElement = ({ video, refresh }: PendingVideoElementProps): React.ReactElement => {
  const [isDeletePopupOpen, setDeletePopupOpen] = useState(false)
  const navigate = useNavigate()
  const theme = useTheme()
  const { open: sbOpen } = useSnackbarContext()
  const progress = useEncodeProgress(video.id, video.processing === 2, refresh)
  const {
    directsData: [direct],
    loading: directLoading,
  } = useDirectAPI({
    constraint: DirectQueryType.DIRECT,
    id: String(video.directId),
  })
  const { tagsData } = useTagAPI({})

  const deleteVideo = async () => {
    try {
      video.processing && (await httpClient.instance.delete(`media/video/${video.id}`))
      await httpClient.instance.delete(`videos/${video.id}`)
    } finally {
      refresh()
    }
  }

  const encodeVideo = async () => {
    if (!video.processing) return
    try {
      await httpClient.instance.post(`encode/${video.id}`)
    } catch {
      sbOpen("Error pendant l'encodage", "error")
    } finally {
      refresh()
    }
  }

  return (
    <Grid container direction="row" key={video.id} borderTop="solid darkgrey 1px">
      <Grid container direction="row" size={3}>
        <Grid size={12}>
          <AspectRatioBox ratio={16 / 9}>
            {video.processing ? (
              <VideoPlayer video={video} raw autoplay={false} disableKeyboardShortcuts onThumbnailChange={refresh} />
            ) : (
              <Typography
                textAlign="center"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                color="error"
              >
                {"Aucune vidéo uploadée"}
              </Typography>
            )}
          </AspectRatioBox>
        </Grid>
        <Grid size={12}>
          <AspectRatioBox ratio={16 / 9}>
            <VideoThumbnailImage video={video} />
          </AspectRatioBox>
        </Grid>
      </Grid>
      <Grid container direction="column" size={9}>
        <Typography component="h3" variant="h3" margin={1}>
          {video.title}
        </Typography>
        <Typography component="p" margin={1}>
          {FormatDate(video.date)}
          {" - "}
          {video.promotion && video.promotion >= 0 ? (
            `P${video.promotion}`
          ) : (
            <span style={{ color: theme.palette.text.secondary }}>{"Aucune promo associée"}</span>
          )}
          {` - Accès : ${["Publique", "CS", "Hyris", "Archivée"][video.privacy]}${video.gpaRestricted ? " - cachée aux GPA" : ""}`}
        </Typography>
        <Typography
          component="p"
          margin={1}
          pl={video.description ? 1 : undefined}
          borderLeft={video.description ? 2 : undefined}
          borderColor={video.description ? theme.palette.soft.main : undefined}
          sx={{ color: video.description ? "text.primary" : "text.secondary" }}
        >
          {video.description ? <FormatParagraphs content={video.description} /> : "Sans description"}
        </Typography>
        <Typography
          component="p"
          margin={1}
          sx={{ color: direct && !directLoading ? "text.primary" : "text.secondary" }}
        >
          {direct || directLoading ? `DT associé : ${direct?.title ?? "..."}` : "Aucun direct associé"}
        </Typography>
        {video.tagIds && video.tagIds.length > 0 ? (
          <Box gap={1} display="flex" margin={1}>
            {tagsData &&
              tagsData.filter((tag) => video.tagIds.includes(tag.id)).map((tag) => <TagChip key={tag.id} tag={tag} />)}
          </Box>
        ) : (
          <Typography component="p" margin={1} color="secondary">
            {"Sans tag"}
          </Typography>
        )}
        {video.processing < 2 ? (
          <>
            <Grid container direction="row">
              <Button
                sx={{ margin: 1 }}
                variant="outlined"
                color="secondary"
                onClick={() => navigate(`/editVideo/${video.id}`)}
              >
                {"Modifier"}
              </Button>
              {video.processing > 0 && (
                <Button sx={{ margin: 1 }} variant="contained" color="secondary" onClick={encodeVideo}>
                  {"Encoder et publier"}
                </Button>
              )}
              <Button
                variant="contained"
                color="error"
                sx={{ margin: 1 }}
                onClick={() => setDeletePopupOpen(true)}
                id={`delete-button${video.id}`}
              >
                {"Supprimer"}
              </Button>
              <Popup
                anchor={document.querySelector(`#delete-button${video.id}`)}
                open={isDeletePopupOpen}
                placement="top"
                offset={10}
              >
                <ClickAwayListener onClickAway={() => setDeletePopupOpen(false)}>
                  <Box
                    padding={2}
                    borderRadius="5px"
                    sx={{
                      backgroundColor: theme.palette.element.contextMenu?.background ?? theme.palette.primary.main,
                      backdropFilter: theme.name === "glass" ? "blur(5px)" : "unset",
                    }}
                  >
                    <Typography>{"Etes-vous sûr de vouloir supprimer cette vidéo ?"}</Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ margin: 1 }}
                      onClick={() => setDeletePopupOpen(false)}
                    >
                      {"Annuler"}
                    </Button>
                    <Button variant="outlined" color="error" onClick={deleteVideo} sx={{ margin: 1 }}>
                      {"Oui, supprimer"}
                    </Button>
                  </Box>
                </ClickAwayListener>
              </Popup>
            </Grid>
          </>
        ) : (
          <Box margin={1}>
            <Typography>
              {progress ? `Encodage en cours (${progress.resolution}) : ${progress.percent}%` : "Encodage en cours..."}
            </Typography>
            <LinearProgress
              variant={progress ? "buffer" : "indeterminate"}
              value={progress?.percent ?? 0}
              valueBuffer={progress ? (RES_PROGRESS_THRESHOLDS[progress.resolution] ?? 0) : 0}
              color="secondary"
              sx={{
                "& .MuiLinearProgress-dashed": {
                  animation: "none",
                  opacity: 0.7,
                },
              }}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  )
}
